import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { firstValueFrom } from 'rxjs';

import { DataService } from '@/services/data.service';
import { NotificationsService } from '@/services/notifications.service';
import { TokenIdParsePipe } from '@/pipes/token-id-parse.pipe';
import { environment } from 'src/environments/environment';

interface PunkLimitRow {
  punkId: number;
  minAmountEth: string;
}

@Component({
  selector: 'app-bid-min-amount-limit-modal',
  standalone: true,
  imports: [CommonModule, FormsModule, TokenIdParsePipe],
  templateUrl: './bid-min-amount-limit-modal.component.html',
  styleUrls: ['./bid-min-amount-limit-modal.component.scss'],
})
export class BidMinAmountLimitModalComponent implements OnChanges {
  @Input() isActive = false;
  @Input() ownerAddress: string | null = null;
  @Output() closed = new EventEmitter<void>();
  @Output() saved = new EventEmitter<void>();

  protected readonly environment = environment;

  loading = false;
  saving = false;
  error: string | null = null;
  rows: PunkLimitRow[] = [];
  globalMinAmount = '';

  constructor(
    public dataSvc: DataService,
    private notificationsService: NotificationsService,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['isActive'] && this.isActive && this.ownerAddress) {
      this.loadData();
    }
    if (changes['isActive'] && !this.isActive) {
      this.reset();
    }
  }

  closeModal(): void {
    this.closed.emit();
  }

  get isGlobalAmountEmpty(): boolean {
    return !this.globalMinAmount || String(this.globalMinAmount).trim().length === 0;
  }

  applyGlobalMinAmount(): void {
    const value = String(this.globalMinAmount).trim();
    this.rows = this.rows.map((row) => ({ ...row, minAmountEth: value }));
  }

  clearAllLimits(): void {
    this.rows = this.rows.map((row) => ({ ...row, minAmountEth: '' }));
    this.globalMinAmount = '';
  }

  async save(): Promise<void> {
    if (this.saving) return;
    this.saving = true;
    this.error = null;

    try {
      const limits: Record<string, number> = {};
      for (const row of this.rows) {
        const val = parseFloat(row.minAmountEth);
        if (!isNaN(val) && val > 0) {
          limits[String(row.punkId)] = val;
        }
      }
      await this.notificationsService.updateBidMinAmountLimits(limits);
      this.saved.emit();
    } catch (err) {
      this.error = 'Failed to save limits. Please try again.';
      console.error('[BidMinAmountLimitModal] Error saving:', err);
    } finally {
      this.saving = false;
    }
  }

  private async loadData(): Promise<void> {
    this.loading = true;
    this.error = null;
    this.rows = [];
    this.globalMinAmount = '';

    try {
      const [ownedPunks, limitsResponse] = await Promise.all([
        firstValueFrom(this.dataSvc.fetchOwned(this.ownerAddress!.toLowerCase())),
        this.notificationsService.getBidMinAmountLimits(),
      ]);

      const limits = limitsResponse.limits || {};
      this.rows = ownedPunks
        .map((punk) => parseInt(punk.id, 10))
        .filter((id) => !isNaN(id))
        .sort((a, b) => a - b)
        .map((punkId) => ({
          punkId,
          minAmountEth: limits[String(punkId)] ? String(limits[String(punkId)]) : '',
        }));
    } catch (err) {
      this.error = 'Failed to load data. Please try again.';
      console.error('[BidMinAmountLimitModal] Error loading:', err);
    } finally {
      this.loading = false;
    }
  }

  private reset(): void {
    this.rows = [];
    this.error = null;
    this.saving = false;
    this.loading = false;
    this.globalMinAmount = '';
  }
}
